'use client'

import React, { useState } from 'react';
import { 
  FaRegSadTear, 
  FaRegSmile, 
  FaRegFrown, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaBook,
  FaLightbulb,
  FaLeaf
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FoodWasteCalculator = () => {
  const [inputs, setInputs] = useState({
    totalFoodWeight: '',
    consumedWeight: '',
    foodCost: ''
  });
  const [results, setResults] = useState(null);
  const [showGraph, setShowGraph] = useState(false);

  const calculateWaste = () => {
    const { totalFoodWeight, consumedWeight, foodCost } = inputs;
    if (!totalFoodWeight || !consumedWeight || !foodCost) return;

    const wasteWeight = totalFoodWeight - consumedWeight;
    const wastePercentage = (wasteWeight / totalFoodWeight) * 100;
    const costWasted = (wasteWeight / totalFoodWeight) * foodCost;

    setResults({
      wasteWeight,
      wastePercentage,
      costWasted,
      environmentalImpact: calculateEnvironmentalImpact(wasteWeight)
    });
    setShowGraph(true);
  };

  const calculateEnvironmentalImpact = (wasteWeight) => {
    // Simplified calculations for demonstration
    const waterWasted = (wasteWeight * 1000) * 0.001; // kg to liters
    const co2Emissions = (wasteWeight * 1000) * 0.0005; // kg to kg CO2
    return {
      waterWasted: Math.round(waterWasted),
      co2Emissions: Math.round(co2Emissions)
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const lineData = {
    labels: ['Total Food', 'Consumed', 'Wasted'],
    datasets: [
      {
        label: 'Food Weight (kg)',
        data: [inputs.totalFoodWeight || 0, inputs.consumedWeight || 0, results?.wasteWeight || 0],
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Food Weight Distribution'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <Helmet>
        <title>Food Waste Calculator | Calculate Food Waste Impact</title>
        <meta name="description" content="Calculate your food waste, its cost, and environmental impact. Track waste weight, percentage, and CO2 emissions." />
        <meta name="keywords" content="food waste calculator, food waste impact, environmental impact calculator, food cost calculator" />
        <meta property="og:title" content="Food Waste Calculator | Calculate Food Waste Impact" />
        <meta property="og:description" content="Calculate your food waste, its cost, and environmental impact. Track waste weight, percentage, and CO2 emissions." />
        <meta property="og:image" content="https://your-image-url.com/food-waste-calculator.jpg" />
      </Helmet>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-green-600 mb-2">
          🍽️ Food Waste Calculator
        </h1>
        <p className="text-gray-600">
          Calculate your food waste and its impact on the environment
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Total Food Weight (kg)
          </label>
          <input
            type="number"
            name="totalFoodWeight"
            value={inputs.totalFoodWeight}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter total food weight"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Consumed Weight (kg)
          </label>
          <input
            type="number"
            name="consumedWeight"
            value={inputs.consumedWeight}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter consumed weight"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Total Food Cost ($)
          </label>
          <input
            type="number"
            name="foodCost"
            value={inputs.foodCost}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter total food cost"
          />
        </div>
      </div>

      <div className="flex gap-4 mb-8">
        <button
          onClick={calculateWaste}
          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
          disabled={!inputs.totalFoodWeight || !inputs.consumedWeight || !inputs.foodCost}
        >
          Calculate Food Waste
        </button>
      </div>

      {showGraph && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Food Waste Distribution</h2>
          <div className="h-64">
            <Line data={lineData} options={lineOptions} />
          </div>
        </div>
      )}

      {results && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 p-6 bg-green-50 rounded-lg border border-green-200"
        >
          <h2 className="text-xl font-semibold mb-4">Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium">Waste Weight</h3>
              <p className="text-green-600 font-semibold">
                {results.wasteWeight.toFixed(2)} kg
              </p>
            </div>
            <div>
              <h3 className="font-medium">Waste Percentage</h3>
              <p className="text-green-600 font-semibold">
                {results.wastePercentage.toFixed(1)}%
              </p>
            </div>
            <div>
              <h3 className="font-medium">Cost Wasted</h3>
              <p className="text-green-600 font-semibold">
                ${results.costWasted.toFixed(2)}
              </p>
            </div>
            <div>
              <h3 className="font-medium">Environmental Impact</h3>
              <p className="text-green-600 font-semibold">
                Water Wasted: {results.environmentalImpact.waterWasted} liters<br />
                CO2 Emissions: {results.environmentalImpact.co2Emissions} kg
              </p>
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-16">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FaBook className="mr-2" />
          How to Use
        </h2>
        <ol className="list-decimal list-inside text-gray-600 space-y-2">
          <li>Enter your total food weight (in kg)</li>
          <li>Input the weight of food you actually consumed</li>
          <li>Enter the total cost of the food</li>
          <li>Click "Calculate Food Waste" to see your results</li>
          <li>View the impact graph to understand your waste distribution</li>
        </ol>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FaLightbulb className="mr-2" />
         Features & Benefits
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="text-green-600 font-semibold mb-2">Save Money</h3>
            <p className="text-gray-600">
              Track wasted food costs and save money by reducing waste
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="text-green-600 font-semibold mb-2">Reduce Environmental Impact</h3>
            <p className="text-gray-600">
              Monitor CO2 emissions and water waste from food disposal
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="text-green-600 font-semibold mb-2">Make Informed Decisions</h3>
            <p className="text-gray-600">
              Understand your food consumption patterns and make better choices
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="text-green-600 font-semibold mb-2">Track Progress</h3>
            <p className="text-gray-600">
              Monitor your waste reduction efforts over time
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FaQuestionCircle className="mr-2" />
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-medium">What is food waste?</h3>
            <p className="text-gray-600">
              Food waste refers to food that is discarded or lost throughout the food supply chain, from production to consumption.
            </p>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-medium">Why is reducing food waste important?</h3>
            <p className="text-gray-600">
              Reducing food waste helps conserve resources, reduce greenhouse gas emissions, and save money. It's a crucial step in building a sustainable food system.
            </p>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-medium">How can I reduce my food waste?</h3>
            <ul className="list-disc list-inside text-gray-600">
              <li>Plan meals and make shopping lists</li>
              <li>Store food properly</li>
              <li>Use leftovers creatively</li>
              <li>Compost food scraps</li>
            </ul>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-medium">How does food waste affect the environment?</h3>
            <p className="text-gray-600">
              Food waste contributes to greenhouse gas emissions, water waste, and loss of valuable resources. When food decomposes in landfills, it produces methane, a potent greenhouse gas.
            </p>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-medium">What can I do with food that's about to expire?</h3>
            <ul className="list-disc list-inside text-gray-600">
              <li>Freeze it for later use</li>
              <li>Make soups or stews with vegetables</li>
              <li>Turn fruits into smoothies or jams</li>
              <li>Donate unopened, non-perishable items to food banks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodWasteCalculator;
